import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { formatCurrency } from "../lib/format";

/**
 * Compact Modelo 303 widget for the Dashboard.
 *
 * Shows the IVA balance for the CURRENT quarter (or last completed if not
 * configurable) plus a yellow alert as the AEAT deadline approaches (day 20
 * of the month after the quarter ends).
 */
export default function Model303Widget() {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const year = now.getFullYear();
  const quarter = Math.floor(now.getMonth() / 3) + 1;

  useEffect(() => {
    let cancelled = false;
    api.get("/tax-books/model-303", { params: { year, quarter } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, quarter]);

  if (loading) {
    return <Skeleton lang={lang} />;
  }
  if (!data) return null;

  const deadline = nextDeadline(year, quarter);
  const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  const inWindow = daysLeft >= 0 && daysLeft <= 20;  // last 20 days before deadline
  const urgent = inWindow && daysLeft <= 7;

  const tone =
    data.result > 0
      ? (urgent ? "rose" : inWindow ? "amber" : "slate")
      : data.result < 0
      ? "emerald"
      : "slate";
  const toneCls = {
    rose: "from-rose-500/15 to-rose-600/5 border-rose-500/30",
    amber: "from-amber-500/15 to-amber-600/5 border-amber-500/30",
    emerald: "from-emerald-500/15 to-emerald-600/5 border-emerald-500/30",
    slate: "from-slate-500/10 to-slate-600/5 border-white/10",
  }[tone];

  const resultColor = {
    rose: "text-rose-300",
    amber: "text-amber-200",
    emerald: "text-emerald-300",
    slate: "text-slate-200",
  }[tone];

  return (
    <div
      className={`card-surface bg-gradient-to-br ${toneCls} p-5 relative overflow-hidden`}
      data-testid="dashboard-303-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center">
            <Calculator size={14} className="text-slate-200" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">
              {lang === "es" ? "Modelo 303 · T" : "Model 303 · T"}{quarter} {year}
            </div>
            <div className="text-[10px] text-slate-500">
              {data.issued_count} {lang === "es" ? "emitidas" : "emeses"} ·{" "}
              {data.received_count} {lang === "es" ? "recibidas" : "rebudes"}
            </div>
          </div>
        </div>
        <Link
          to="/app/tax-books"
          className="text-[11px] text-slate-300 hover:text-white flex items-center gap-1 rounded-md px-2 py-1 hover:bg-white/5 transition-colors"
          data-testid="dashboard-303-cta"
        >
          {lang === "es" ? "Detalle" : "Detall"}
          <ArrowRight size={11} />
        </Link>
      </div>

      <div className="flex items-end justify-between gap-3 mb-1">
        <div>
          <div className={`text-3xl font-bold tabular-nums ${resultColor}`}>
            {formatCurrency(Math.abs(data.result), lang)}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {data.result > 0
              ? (lang === "es" ? "A ingresar en Hacienda" : "A ingressar a Hisenda")
              : data.result < 0
              ? (lang === "es" ? "A compensar / devolver" : "A compensar / retornar")
              : (lang === "es" ? "Saldo neutro" : "Saldo neutre")}
          </div>
        </div>

        <div className="text-right text-[10px] text-slate-400 leading-tight">
          <div className="flex items-center justify-end gap-1">
            <span className="text-emerald-400">↑</span>
            <span className="font-mono">{formatCurrency(data.total_charged, lang)}</span>
          </div>
          <div className="flex items-center justify-end gap-1 mt-0.5">
            <span className="text-blue-400">↓</span>
            <span className="font-mono">{formatCurrency(data.total_supported, lang)}</span>
          </div>
        </div>
      </div>

      {/* Deadline strip */}
      <div className="mt-3 pt-3 border-t border-white/10">
        {data.result > 0 && inWindow ? (
          <div className={`flex items-center gap-2 text-xs ${urgent ? "text-rose-300" : "text-amber-200"}`}>
            <AlertTriangle size={12} />
            <span className="font-medium">
              {daysLeft === 0
                ? (lang === "es" ? "¡Vence HOY!" : "Venç AVUI!")
                : daysLeft === 1
                ? (lang === "es" ? "Vence mañana" : "Venç demà")
                : (lang === "es"
                  ? `Vence en ${daysLeft} días`
                  : `Venç en ${daysLeft} dies`)}
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-400">{formatShortDate(deadline, lang)}</span>
          </div>
        ) : data.result === 0 ? (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <CheckCircle2 size={12} />
            {lang === "es" ? "Sin operaciones este trimestre" : "Sense operacions aquest trimestre"}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>{lang === "es" ? "Próxima presentación AEAT:" : "Propera presentació AEAT:"}</span>
            <span className="text-slate-300">{formatShortDate(deadline, lang)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Skeleton({ lang }) {
  return (
    <div className="card-surface p-5 animate-pulse">
      <div className="text-xs uppercase tracking-wider text-slate-500">
        {lang === "es" ? "Cargando Modelo 303..." : "Carregant Model 303..."}
      </div>
      <div className="h-8 bg-white/5 rounded mt-3 w-40" />
      <div className="h-3 bg-white/5 rounded mt-3 w-full" />
    </div>
  );
}

/**
 * AEAT deadline: day 20 of the month after the quarter ends.
 *   Q1 (Jan-Mar) → 20 April
 *   Q2 (Apr-Jun) → 20 July
 *   Q3 (Jul-Sep) → 20 October
 *   Q4 (Oct-Dec) → 30 January next year (annual + Modelo 390)
 */
function nextDeadline(year, quarter) {
  if (quarter === 4) return new Date(year + 1, 0, 30);  // 30 Jan next year
  const month = quarter * 3;  // 0-based: Q1→3 (Apr), Q2→6 (Jul), Q3→9 (Oct)
  return new Date(year, month, 20);
}

function formatShortDate(d, lang) {
  return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "ca-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}
